import { useEffect, useMemo, useState } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import type { ChannelProgramIndex } from "../epg/hooks.js";
import { artworkKey, artworkRequestFor, useHistoryArtwork } from "./historyArtwork.js";
import { useViewerCountry } from "../settings/countryStore.js";
import { eligibleChannels, localMarketFrom, pickLiveNow, type LiveCandidate } from "./liveNowPicks.js";

/**
 * Home's "Live Now" rail (owner, 2026-10-12: it should rotate among the most-watched US
 * channels, never a 24/7 loop, live sports twice as likely, local stations only for the
 * viewer's own market). WHAT is featured is decided in liveNowPicks.ts; this hook asks the
 * viewer's own guide what each of those channels has on now, and fetches a picture for it.
 *
 * It used to match TVmaze's schedule to channels by network name, with a TMDB trending
 * fill: the name match put an Italian Serie A event feed in as "Good Morning America" and
 * NFL team packages as "Today", and nothing knew a 24/7 loop from live television.
 *
 * Neither the guide nor the pictures contact the viewer's IPTV provider: browsing costs
 * zero provider connections. Clicking a tile tunes the channel (one connection, the normal
 * tune path). The picture is the show's: TVmaze's search under Open, TMDB's otherwise
 * (historyArtwork.ts), else the channel's logo.
 */

export interface ExternalLiveNowTile {
  channel: Channel;
  showName: string;
  episodeName?: string;
  networkName: string;
  start: string; // ISO
  end: string; // ISO
  imageUrl?: string;
  /** The viewer's own guide says this is on now. */
  confirmed: boolean;
  /** Live sports (the rail's 2x boost): a game or event, not a studio show. */
  sports: boolean;
}

const MAX_TILES = 12;
const REFRESH_MS = 60 * 1000; // re-evaluate the "now" window every minute
/** How long the loading skeleton waits for a guide that may never come. */
const GUIDE_GIVE_UP_MS = 20_000;
const NO_NAMES: readonly string[] = [];

/**
 * A ticking clock. Any "as of now" derivation (Live Now's airing window,
 * Recently Watched's now-playing program, progress bars) should read from
 * this instead of a one-shot Date.now() in a memo, so the UI advances as
 * time passes — both on a 60s timer and the instant the tab regains focus
 * (a user returning to the app shouldn't see a show that ended while they
 * were away).
 */
export function useNowClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), REFRESH_MS);
    const onFocus = () => setNow(Date.now());
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return now;
}

const NO_TILES: ExternalLiveNowTile[] = [];

export function useExternalLiveNow(
  channels: Channel[],
  programIndex: ChannelProgramIndex | null,
  /** False when the row is switched off: nothing is matched and nothing is fetched. */
  enabled = true,
  /** The names of the stations the viewer favourites and watches: where their home market is read from. */
  watchedNames: readonly string[] = NO_NAMES,
): { tiles: ExternalLiveNowTile[]; isLoading: boolean; isError: boolean } {
  const now = useNowClock();
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!enabled || programIndex) return;
    const timer = window.setTimeout(() => setGaveUp(true), GUIDE_GIVE_UP_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, programIndex]);

  // Once per channel list, never per tick: the few hundred channels that could ever be featured.
  const country = useViewerCountry();
  const eligible = useMemo(
    () => (enabled && channels.length > 0 ? eligibleChannels(channels, country) : []),
    [enabled, channels, country],
  );
  const market = useMemo(() => localMarketFrom(watchedNames), [watchedNames]);

  const picks = useMemo(() => {
    if (!enabled || !programIndex || eligible.length === 0) return [];
    const at = new Date(now);
    const candidates: Array<LiveCandidate<Channel, Program>> = [];
    for (const entry of eligible) {
      const program = programIndex.getNowPlaying(entry.channel.id, at);
      if (!program?.title) continue;
      candidates.push({ eligible: entry, program });
    }
    return pickLiveNow(candidates, { now, max: MAX_TILES, market });
  }, [enabled, programIndex, eligible, now, market]);

  const artRequests = useMemo(() => picks.map((pick) => artworkRequestFor("channel", pick.program.title)), [picks]);
  const art = useHistoryArtwork(artRequests);

  const tiles = useMemo<ExternalLiveNowTile[]>(
    () =>
      picks.map((pick, index) => {
        const request = artRequests[index];
        const subtitle = pick.program.subtitle?.trim();
        const imageUrl = request ? art.get(artworkKey(request)) : undefined;
        return {
          channel: pick.channel,
          showName: pick.program.title,
          ...(subtitle && subtitle.toLowerCase() !== pick.program.title.toLowerCase() ? { episodeName: subtitle } : {}),
          networkName: pick.channel.name,
          start: pick.program.start,
          end: pick.program.end,
          ...(imageUrl ? { imageUrl } : {}),
          confirmed: true,
          sports: pick.sports,
        };
      }),
    [picks, artRequests, art],
  );

  // Switched off: no row, nothing fetched, and none of the matching above ran.
  if (!enabled) return { tiles: NO_TILES, isLoading: false, isError: false };
  return { tiles, isLoading: programIndex === null && !gaveUp, isError: false };
}
